import Decimal from "decimal.js";

export const RBM_REPORT_METHOD = [
  {
    value: "quarterly",
    label: "Quarterly",
  },
  {
    value: "trimester",
    label: "Trimester",
  },
  {
    value: "monthly",
    label: "Monthly",
  },
];

export const FORMULA_TYPES_ARRAY = [
  { label: "SUM", value: "SUM" },
  { label: "Incrementing Percentage", value: "%UP" },
  { label: "Decrementing Percentage", value: "%DOWN" },
  { label: "Repeated Fixed Target", value: "REPEAT" },
  // { label: "Yes/No", value: "YN" },
  // { label: "Fixed Target", value: "FIXED" },
  // { label: "Range Target", value: "RANGE" },
  // { label: "Ranking", value: "RANK" },
];

export const MEASUREMENT_NAME_TO_CODE = {
  "Incrementing Percentage": "%UP",
  "Decrementing Percentage": "%DOWN",
  "Yes/No": "YN",
  SUM: "SUM",
  "Fixed Target": "FIXED",
  "Repeated Fixed Target": "REPEAT",
  "Range Target": "RANGE",
  Ranking: "RANK",
};

// validate weight of a provided field if are High, Medium or Low
export const isWeightValid = (value) => {
  return ["High", "Medium", "Low"].includes(value);
};

export const FC_AreDataValid = (data) => {
  const errors = [];

  const groupByIndicator = {};
  for (const row of data) {
    const key = `${row.outputNo}-${row.indicatorNo}`;
    if (!groupByIndicator[key]) groupByIndicator[key] = [];
    groupByIndicator[key].push(row);
  }

  for (const [key, records] of Object.entries(groupByIndicator)) {
    const {
      outputNo,
      indicatorNo,
      formulaType,
      measurementUnit,
      annualTarget,
      q1Target,
      q2Target,
      q3Target,
      q4Target,
    } = records[0];

    // --- Validate formulaType ---
    if (
      !formulaType ||
      !Object.keys(MEASUREMENT_NAME_TO_CODE).includes(formulaType)
    ) {
      errors.push({
        type: "invalid-formula",
        message: `Output ${outputNo}, Indicator ${indicatorNo}: Invalid or missing FormulaType "${formulaType}".`,
      });
      continue;
    }

    // --- Optional: Validate measurementUnit exists ---
    if (!measurementUnit || typeof measurementUnit !== "string") {
      errors.push({
        type: "missing-unit",
        message: `Output ${outputNo}, Indicator ${indicatorNo}: MeasurementUnit is required.`,
      });
    }

    const unitCode = formulaType;
    const qTargets = { 1: q1Target, 2: q2Target, 3: q3Target, 4: q4Target };

    const toDecimal = (v) =>
      v !== undefined && v !== null ? new Decimal(v) : new Decimal(0);

    const qDec = {
      1: toDecimal(q1Target),
      2: toDecimal(q2Target),
      3: toDecimal(q3Target),
      4: toDecimal(q4Target),
    };

    const aDec = toDecimal(annualTarget);

    const getSum = (list) =>
      list.reduce((sum, r) => sum.plus(r.targetShare || 0), new Decimal(0));

    // --- COMMON RANGE VALIDATION ---
    if (["%UP", "%DOWN", "REPEAT"].includes(unitCode)) {
      const outOfRange = [
        q1Target,
        q2Target,
        q3Target,
        q4Target,
        annualTarget,
      ].some((v) => isNaN(v) || v < 0 || v > 100);
      if (outOfRange) {
        errors.push({
          type: "value-range",
          message: `Output ${outputNo}, Indicator ${indicatorNo}: All values for ${unitCode} must be between 0 and 100.`,
        });
      }
    }

    // === INDICATOR VALIDATION ===
    switch (unitCode) {
      case "%UP":
        {
          const sum = qDec[1].plus(qDec[2]).plus(qDec[3]).plus(qDec[4]);
          if (!sum.equals(aDec)) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: Q1–Q4 total (${sum}) must equal AnnualTarget (${aDec}).`,
            });
          }
          if (q1Target > q2Target || q2Target > q3Target) {
            errors.push({
              type: "trend-error",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: For %UP, Q1 ≤ Q2 ≤ Q3 required. Q4 may be 0.`,
            });
          }
        }
        break;

      case "%DOWN":
        {
          const sum = qDec[1].plus(qDec[2]).plus(qDec[3]).plus(qDec[4]);
          if (!sum.equals(aDec)) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: Q1–Q4 total (${sum}) must equal AnnualTarget (${aDec}).`,
            });
          }
          if (q2Target < q3Target || q3Target < q4Target) {
            errors.push({
              type: "trend-error",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: For %DOWN, Q2 ≥ Q3 ≥ Q4 required. Q1 may be 0.`,
            });
          }
        }
        break;

      case "SUM":
      case "POINTS":
        {
          const sum = qDec[1].plus(qDec[2]).plus(qDec[3]).plus(qDec[4]);
          if (!sum.equals(aDec)) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: Q1–Q4 total (${sum}) must equal AnnualTarget (${aDec}).`,
            });
          }
        }
        break;

      case "MS":
        {
          const sum = qDec[1].plus(qDec[2]).plus(qDec[3]).plus(qDec[4]);
          if (sum.gt(aDec)) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: Milestone total (${sum}) must not exceed AnnualTarget (${aDec}).`,
            });
          }
        }
        break;

      case "REPEAT":
        {
          const mustEqual = [q1Target, q2Target, q3Target].every(
            (v) => +v === +annualTarget,
          );
          const q4Okay = +q4Target === 0 || +q4Target === +annualTarget;

          if (!mustEqual || !q4Okay) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: For REPEAT, Q1–Q3 must equal AnnualTarget (${annualTarget}). Q4 may be 0 or equal.`,
            });
          }
        }
        break;

      case "FIXED":
        {
          const nonZeroQuarters = [
            q1Target,
            q2Target,
            q3Target,
            q4Target,
          ].filter((v) => +v > 0);
          if (nonZeroQuarters.length !== 1) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: FIXED target must appear in one quarter only.`,
            });
          }
        }
        break;

      case "YN":
        {
          const invalid = [
            q1Target,
            q2Target,
            q3Target,
            q4Target,
            annualTarget,
          ].some((v) => v !== 0 && v !== 1);
          if (invalid) {
            errors.push({
              type: "indicator-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}: All YN values must be 0 or 1.`,
            });
          }
        }
        break;
    }

    // === ACTIVITY VALIDATION ===
    for (let q = 1; q <= 4; q++) {
      const qRows = records.filter((r) => r.activityQuarterNo === q);
      const qTarget = qDec[q];
      const sumShare = getSum(qRows);

      switch (unitCode) {
        case "%UP":
        case "%DOWN":
        case "SUM":
        case "POINTS":
        case "REPEAT":
          if (!sumShare.equals(qTarget)) {
            errors.push({
              type: "activity-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}, Q${q}: TargetShare sum (${sumShare}) must equal Q${q}Target (${qTarget}).`,
            });
          }
          break;

        case "REPEAT": {
          const invalid = qRows.some(
            (r) =>
              +r.targetShare !== 0 && +r.targetShare !== qTarget.toNumber(),
          );
          if (invalid) {
            errors.push({
              type: "activity-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}, Q${q}: Each TargetShare must be 0 or match Q${q}Target (${qTarget}).`,
            });
          }
          break;
        }

        case "FIXED":
          if (qTarget.gt(0) && qRows.length > 1) {
            errors.push({
              type: "activity-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}, Q${q}: Only one activity allowed for FIXED target.`,
            });
          }
          break;

        case "YN":
          if (
            qRows.length &&
            qTarget.toNumber() !== 0 &&
            qTarget.toNumber() !== 1
          ) {
            errors.push({
              type: "activity-validation",
              message: `Output ${outputNo}, Indicator ${indicatorNo}, Q${q}: YN target must be 0 or 1.`,
            });
          }
          break;
      }
    }
  }

  return errors;
};

/**
 * Validate activities based on measurement unit rules
 * @param {Object} quarter - Quarter object
 * @param {Object} selectedActivity - Selected activity object
 * @param {number} totalQuarterTarget - Sum of targetShares
 * @returns {string|null} - Returns error message if invalid, otherwise null
 */
export const validateActivities = (quarter, selectedActivity) => {
  const { target, activities } = quarter;
  const { measurementUnitCode } = selectedActivity;

  switch (measurementUnitCode) {
    // SUM / %UP / %DOWN -> total of all activities must equal target
    case "%UP":
    case "%DOWN":
    case "SUM": {
      const sum = activities.reduce(
        (acc, a) => acc + Number(a.targetShare || 0),
        0,
      );
      if (sum !== target) {
        return `Target mismatch: total activity shares = ${sum}, but quarter target = ${target}`;
      }
      break;
    }

    // REPEAT -> each activity must be equal to target or 0
    case "REPEAT": {
      const invalid = activities.find(
        (a) => !(a.targetShare === 0 || a.targetShare === target),
      );
      const sum = !!invalid
        ? activities.reduce((acc, a) => acc + Number(a.targetShare || 0), 0)
        : -1;
      if (invalid && sum !== target) {
        return `Activity "${invalid.name}" must have targetShare = ${target} or 0`;
      }
      break;
    }

    // FIXED -> each activity must equal target, between 0–100, or 0
    case "FIXED": {
      const invalid = activities.find((a) => {
        const share = Number(a.targetShare || 0);
        return !(
          share === 0 ||
          (share === target && share >= 0 && share <= 100)
        );
      });
      if (invalid) {
        return `Activity "${invalid.name}" must have targetShare = ${target} (0–100) or 0`;
      }
      break;
    }

    default:
      return null; // No validation rules for other types
  }

  // Also check remaining total against quarter target
  // if (Number(quarter.target) - Number(totalQuarterTarget) !== 0) {
  //   const remaining = Number(quarter.target) - Number(totalQuarterTarget);
  //   return `${remaining} Target${remaining > 1 ? "s" : ""} remaining`;
  // }

  return null; // all good
};
